/**
 * A bot's avatar: upload an image, paste an http(s) URL, or remove it.
 *
 * Shared by the Edit API key modal and a webhook row's inline editor, both of
 * which give a bot identity a face. The image is sent the moment it is chosen
 * — the same immediate-upload pattern as the Space logo in SettingsPage.tsx —
 * rather than waiting for a surrounding form to be saved, because setting an
 * avatar is a one-off action, not a field on a multi-field form.
 *
 * This component never talks to the network itself: the three actions are
 * handed in as callbacks so the caller can pick the right identifiers (an API
 * key id, or a room id and webhook id) and invalidate the right cache entry.
 * That also keeps this file usable from both an admin page and a channel's
 * webhook manager without either pulling in the other's dependencies.
 */

import { useEffect, useId, useState } from 'react'
import type { ChangeEvent, DragEvent as ReactDragEvent, KeyboardEvent, ReactElement } from 'react'

import { isApiError } from '@/api/client'
import { Avatar, Button, TextField } from '@/components/ui'
import { externalImageSrc, useExternalImages } from '@/lib/externalImages'
import styles from './BotAvatarField.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

/** What the finalize/remove/set-URL endpoints accept. Checked here so a doomed upload never starts. */
const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif']
const AVATAR_MAX_BYTES = 5 * 1024 * 1024

/** The first thing wrong with a chosen file, in the words the field will use. */
function avatarProblemFor(file: File): string | null {
  if (!AVATAR_TYPES.includes(file.type)) return 'Choose a PNG, JPEG, GIF, WebP or AVIF image.'
  if (file.size > AVATAR_MAX_BYTES) return 'The image must be 5 MB or smaller.'
  if (file.size === 0) return 'Choose an image that is not empty.'
  return null
}

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

type Busy = 'upload' | 'remove' | 'url' | null

export interface BotAvatarFieldProps {
  /** The bot's current name. Feeds the avatar's initials fallback and its label. */
  name: string
  /** The server's current image, or null for the default bot avatar. */
  currentUrl: string | null
  /** Uploads the file and returns the resulting avatar URL (or null). */
  onUpload: (file: File, onProgress: (fraction: number) => void) => Promise<string | null>
  /** Removes the current image and returns the resulting avatar URL (always null on success). */
  onRemove: () => Promise<string | null>
  /** Sets the image by URL and returns the resulting avatar URL. */
  onSetUrl: (url: string) => Promise<string | null>
  /**
   * A line under the label, read out as the group's description. The API key
   * modal uses it to say the avatar is saved at once, unlike the fields
   * around it that wait for Save.
   */
  hint?: string
  disabled?: boolean
  className?: string
}

export function BotAvatarField({
  name,
  currentUrl,
  onUpload,
  onRemove,
  onSetUrl,
  hint,
  disabled = false,
  className,
}: BotAvatarFieldProps): ReactElement {
  // Undefined means "nothing has happened here yet — show what the caller
  // passed in." Once an action succeeds, the field shows its own result
  // immediately rather than waiting for the caller's data to refetch. Once
  // the caller's own value moves on — a refetch, another tab, another admin —
  // the effect below drops back to undefined so that value is shown again;
  // between a successful action and that refetch, the local value still wins.
  const [preview, setPreview] = useState<string | null | undefined>(undefined)
  const [busy, setBusy] = useState<Busy>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [urlValue, setUrlValue] = useState('')
  const [dropTarget, setDropTarget] = useState(false)
  const labelId = useId()
  const hintId = useId()
  const allowExternalImages = useExternalImages()

  useEffect(() => {
    setPreview(undefined)
  }, [currentUrl])

  // A pasted external URL is only drawn once the viewer has allowed external
  // images — same rule as `userAvatarSrc` — whether it came from `currentUrl`
  // or from this field's own just-set preview. An uploaded avatar is always
  // same-origin, so this never holds one back.
  const rawShownUrl = preview !== undefined ? preview : currentUrl
  const shownUrl = externalImageSrc(rawShownUrl, allowExternalImages)
  const busyAny = busy !== null || disabled

  const upload = async (file: File): Promise<void> => {
    const trouble = avatarProblemFor(file)
    if (trouble !== null) {
      setProblem(trouble)
      return
    }

    setProblem(null)
    setStatus(null)
    setBusy('upload')
    setProgress(0)

    try {
      const avatarUrl = await onUpload(file, setProgress)
      setPreview(avatarUrl)
      setStatus('Avatar updated.')
    } catch (error: unknown) {
      setProblem(describe(error, 'The image could not be uploaded.'))
    } finally {
      setBusy(null)
      setProgress(null)
    }
  }

  const chooseFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null
    // Allow choosing the same file again after correcting a validation error.
    event.target.value = ''
    if (file !== null) void upload(file)
  }

  const droppedFiles = (event: ReactDragEvent<HTMLLabelElement>): boolean =>
    Array.from(event.dataTransfer.types).includes('Files')

  const onDragOver = (event: ReactDragEvent<HTMLLabelElement>): void => {
    if (busyAny || !droppedFiles(event)) return
    event.preventDefault()
    setDropTarget(true)
  }

  const onDrop = (event: ReactDragEvent<HTMLLabelElement>): void => {
    event.preventDefault()
    setDropTarget(false)
    if (busyAny) return
    // Drag and drop ignores the file picker's `accept`, so the type check in
    // `upload` is the only thing standing between an arbitrary file and a
    // doomed upload session.
    const file = event.dataTransfer.files[0] ?? null
    if (file !== null) void upload(file)
  }

  const remove = async (): Promise<void> => {
    setProblem(null)
    setStatus(null)
    setBusy('remove')

    try {
      const avatarUrl = await onRemove()
      setPreview(avatarUrl)
      setStatus('Avatar removed.')
    } catch (error: unknown) {
      setProblem(describe(error, 'The image could not be removed.'))
    } finally {
      setBusy(null)
    }
  }

  const setUrl = async (): Promise<void> => {
    const trimmed = urlValue.trim()
    if (trimmed === '') {
      setProblem('Paste an image URL first.')
      return
    }

    setProblem(null)
    setStatus(null)
    setBusy('url')

    try {
      const avatarUrl = await onSetUrl(trimmed)
      setPreview(avatarUrl)
      setUrlValue('')
      setStatus('Avatar updated.')
    } catch (error: unknown) {
      setProblem(describe(error, 'That URL could not be used.'))
    } finally {
      setBusy(null)
    }
  }

  // This field lives inside the Edit API key modal's <form>. Enter in a text
  // input triggers the browser's implicit form submission unless the keydown
  // itself is prevented — stopping propagation is not enough on its own.
  const onUrlKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (busy === null && !disabled) void setUrl()
  }

  return (
    <div
      className={cx(styles.field, className)}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={hint === undefined ? undefined : hintId}
    >
      <span className={styles.label} id={labelId}>
        Bot avatar
      </span>
      {hint === undefined ? null : (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}
      <div className={styles.row}>
        <Avatar name={name} src={shownUrl} size="lg" decorative />
        <div className={styles.controls}>
          <label
            className={cx(styles.dropzone, dropTarget && styles.dropzoneActive, busyAny && styles.dropzoneDisabled)}
            onDragOver={onDragOver}
            onDragLeave={() => setDropTarget(false)}
            onDrop={onDrop}
          >
            <input
              type="file"
              aria-label={`Upload a bot avatar for ${name}`}
              accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
              onChange={chooseFile}
              disabled={busyAny}
            />
            <span>{busy === 'upload' ? 'Uploading…' : dropTarget ? 'Drop image to upload' : 'Upload image…'}</span>
          </label>
          {rawShownUrl !== null ? (
            <Button
              type="button"
              size="sm"
              disabled={busyAny}
              loading={busy === 'remove'}
              onClick={() => void remove()}
            >
              Remove avatar
            </Button>
          ) : null}
        </div>
      </div>

      {progress === null ? null : (
        <div
          className={styles.progress}
          role="progressbar"
          aria-label="Avatar upload"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <span style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}

      <div className={styles.urlRow}>
        <TextField
          label="Or use an image URL"
          placeholder="https://example.com/avatar.png"
          value={urlValue}
          disabled={busyAny}
          onChange={(event) => setUrlValue(event.target.value)}
          onKeyDown={onUrlKeyDown}
        />
        <Button
          type="button"
          size="sm"
          disabled={busyAny || urlValue.trim() === ''}
          loading={busy === 'url'}
          onClick={() => void setUrl()}
        >
          Use this URL
        </Button>
      </div>

      {problem === null ? null : (
        <p className={styles.error} role="alert">
          {problem}
        </p>
      )}

      <span className="sr-only" role="status">
        {status ?? ''}
      </span>
    </div>
  )
}

export default BotAvatarField
